import React, { FC, useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogActions, TextField, Button, IconButton, Checkbox, FormControlLabel, Tooltip, InputAdornment } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations, getLanguage } from '../../store/translations/hooks';
import styled from 'styled-components';
import ListAvatar from '../ListAvatar/ListAvatar';
import { GoogleSignInButton } from '../OAuthButtons';

const BRAND_PRIMARY = 'var(--appGreen)';
const BRAND_SECONDARY = 'var(--appGreen-hover)';
const BRAND_GRADIENT = 'linear-gradient(90deg, #08b180 0%, #209b97 35%, #49b2e5 100%)';
const MAX_PROFESSIONALS = 10;
const TOOLTIP_TOUCH_PROPS = {
    enterTouchDelay: 0,
    leaveTouchDelay: 2500,
};

const StyledTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} classes={{ tooltip: className }} />
))`
    font-size: 0.875rem !important;
    padding: var(--spacer-S) !important;
    max-width: 300px !important;
    line-height: 1.4 !important;
    background-color: rgba(55, 61, 63, 0.95) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
`;

const WizardContainer = styled.div`
    min-height: 300px;
    position: relative;
`;

const StepHeader = styled.div`
    text-align: center;
    margin-bottom: var(--spacer-M);

    h3 {
        color: ${BRAND_SECONDARY};
        font-size: 1.5rem;
        margin-bottom: var(--spacer-XS);
    }

    p {
        color: var(--text-muted);
        font-size: 0.9rem;
    }
`;

const StepIndicator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: var(--spacer-M);
    gap: var(--spacer-S);
`;

const StepCircle = styled.div<React.PropsWithChildren<{ $active: boolean; $completed: boolean }>>`
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    background: ${props => props.$active || props.$completed ? BRAND_GRADIENT : 'var(--appLigthGrey)'};
    color: ${props => props.$active || props.$completed ? 'white' : 'var(--text-muted)'};
    transition: all 0.3s;
    position: relative;
    font-size: 1.2rem;
    box-shadow: ${props => props.$active || props.$completed ? '0 10px 22px rgba(0, 147, 133, 0.28)' : 'none'};
`;

const StepNumber = styled.span``;

const StepLine = styled.div<{ $completed: boolean }>`
    width: 3rem;
    height: var(--border-medium);
    background: ${props => props.$completed ? BRAND_GRADIENT : 'var(--appLigthGrey)'};
    transition: all 0.3s;
`;

const ErrorText = styled.div`
    color: var(--danger-color);
    font-size: 0.875rem;
    margin-top: var(--spacer-XXS);
`;

const HelperText = styled.p`
    color: var(--text-muted);
    font-size: 0.875rem;
    margin: var(--spacer-XS) 0 0;
`;

const CloseButton = styled(IconButton)`
    color: var(--text-muted) !important;
`;

const WizardHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: var(--spacer-XXS);
    flex: 0 0 auto;
`;

const AdminSectionTitle = styled.h4`
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-body);
    margin: 0 0 var(--spacer-M) 0;
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const AdminGoogleCard = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    padding: var(--spacer-M);
    background: var(--bg-light);
    border-radius: var(--spacer-XS);
    border: var(--border-thin) solid var(--appLigthGrey);
`;

const AdminInfo = styled.div`
    flex: 1;
`;

const AdminCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--spacer-S);
`;

const AdminAuthBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: var(--spacer-XXS);
    padding: 0.2rem 0.55rem;
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 999px;
    color: var(--text-muted);
    font-size: 0.75rem;
`;

const AdminAuthBadgeIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: #f2f2f2;
    color: #4285f4;
    font-weight: 700;
    font-size: 0.7rem;
`;

const EditAuthButton = styled(IconButton)`
    color: var(--text-muted) !important;
`;

const GoogleButtonWrapper = styled.div`
    width: 100%;
    max-width: 360px;
    margin-bottom: var(--spacer-S);
`;

const AuthDivider = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
    margin: var(--spacer-S) 0;
    color: var(--text-muted);
    font-size: 0.85rem;

    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: var(--border-thin) solid var(--appLigthGrey);
    }

    &::before {
        margin-right: var(--spacer-S);
    }

    &::after {
        margin-left: var(--spacer-S);
    }
`;

const AdminLabel = styled.div`
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    color: ${BRAND_SECONDARY};
    margin-bottom: var(--spacer-XXS);
`;

const AdminName = styled.div`
    font-weight: 600;
    color: var(--text-body);
    margin-bottom: var(--spacer-XXS);
    font-size: 0.875rem;
`;

const AdminEmail = styled.div`
    color: var(--text-muted);
    font-size: 0.8rem;
`;

const SectionTitle = styled.h4`
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-body);
    margin: 0 0 var(--spacer-S) 0;
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const InfoIconButton = styled(IconButton)`
    color: var(--text-muted) !important;
    padding: 0 !important;
    font-size: 1rem !important;
`;

const StyledTextField = styled(TextField)`
    &:not(:last-of-type) {
        margin-bottom: var(--spacer-S);
    }

    & .MuiInputBase-root {
        font-size: 0.875rem;
    }

    & .MuiInputBase-input {
        padding: var(--spacer-S);
    }

    & .MuiInputBase-input::placeholder {
        font-size: 0.875rem;
    }

    & .MuiInputLabel-root {
        font-size: 0.875rem;
    }
`;

const Step3TextField = styled(TextField)`
    & .MuiInputBase-root {
        font-size: 0.875rem;
    }

    & .MuiInputBase-input {
        padding: var(--spacer-S);
    }

    & .MuiInputBase-input::placeholder {
        font-size: 0.875rem;
    }

    & .MuiInputLabel-root {
        font-size: 0.875rem;
    }
`;

const CheckboxContainer = styled.div<React.PropsWithChildren<{ style?: React.CSSProperties }>>`
    margin-top: var(--spacer-M);
    padding-top: var(--spacer-S);
    border-top: var(--border-thin) solid var(--appLigthGrey);
`;

const AddButtonContainer = styled.div`
    margin-top: var(--spacer-XS);
    margin-bottom: var(--spacer-XS);
`;

const ButtonStyled = styled(Button)`
    color: ${BRAND_SECONDARY} !important;
    font-weight: 600 !important;
    text-transform: none !important;
    border-radius: 999px !important;
    padding: var(--spacer-XS) var(--spacer-S) !important;
    border-color: ${BRAND_PRIMARY} !important;

    &:hover {
        background: rgba(42, 183, 175, 0.12) !important;
        border-color: ${BRAND_PRIMARY} !important;
    }
`;

const DialogActionsStyled = styled(DialogActions)`
    padding: var(--spacer-M);
    justify-content: space-between;
    flex: 0 0 auto;
`;

const DialogContentStyled = styled(DialogContent)`
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    padding-top: var(--spacer-M);
    flex: 1 1 auto;
`;

const HeroContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-M);
`;

const HeroTitle = styled.h2`
    margin: 0;
    background: ${BRAND_GRADIENT};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    font-size: 2.4rem;
    letter-spacing: 0.03em;
    text-align: center;

    @media (max-width: 440px) {
        font-size: 2rem;
    }
`;

const TrialBadge = styled.div<React.PropsWithChildren<{}>>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacer-XS);
    padding: 0.6rem 1.2rem;
    border-radius: 999px;
    background: ${BRAND_GRADIENT};
    color: white;
    box-shadow: 0 14px 30px rgba(0, 147, 133, 0.28);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 500;
    font-size: 0.7rem;
    margin: 10px;
`;

const PrimaryActionButton = styled(Button)`
    background: ${BRAND_GRADIENT} !important;
    color: white !important;
    padding: 0.6rem 1.6rem !important;
    border-radius: 0.4rem !important;
    font-weight: 600 !important;
    text-transform: none !important;
    box-shadow: none !important;
    border: none !important;

    &:hover {
        filter: brightness(0.97);
    }
`;

const SectionCard = styled.div`
    background: white;
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    padding: var(--spacer-M);
    box-shadow: 0 10px 30px rgba(0, 147, 133, 0.06);
    margin-bottom: var(--spacer-M);
`;

const SuggestionGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-XS);
    margin: var(--spacer-S) 0;
`;

const SuggestionButton = styled(Button)<React.PropsWithChildren<{ $selected?: boolean }>>`
    border-radius: 999px !important;
    border-color: ${props => props.$selected ? BRAND_PRIMARY : 'var(--appLigthGrey)'} !important;
    color: ${props => props.$selected ? BRAND_SECONDARY : 'var(--text-muted)'} !important;
    text-transform: none !important;
    padding: 0.3rem 0.9rem !important;
    background: ${props => props.$selected ? 'rgba(42, 183, 175, 0.07)' : 'var(--bg-light)'} !important;
    transition: all 0.2s ease !important;

    &:hover {
        border-color: ${BRAND_PRIMARY} !important;
        background: rgba(42, 183, 175, 0.12) !important;
    }
`;

const AddCustomContainer = styled.div<React.PropsWithChildren<{ $fullWidth?: boolean }>>`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    flex: ${props => props.$fullWidth ? '1 1 100%' : '0 0 auto'};
    width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

const AddCustomInput = styled(TextField)`
    flex: 1;
    min-width: 0;

    & .MuiInputBase-root {
        border-radius: 999px;
        font-size: 0.875rem;
        background: white;
        height: 2rem;
    }

    & .MuiInputBase-input {
        padding: 0.4rem 0.9rem;
    }

    & .MuiOutlinedInput-root {
        & fieldset {
            border-color: var(--appLigthGrey);
        }
        &:hover fieldset {
            border-color: ${BRAND_PRIMARY};
        }
        &.Mui-focused fieldset {
            border-color: ${BRAND_PRIMARY};
        }
    }
`;

const AddButton = styled(IconButton)`
    background: var(--bg-light) !important;
    border: var(--border-thin) solid var(--appLigthGrey) !important;
    color: ${BRAND_SECONDARY} !important;
    width: 2rem !important;
    height: 2rem !important;
    font-size: 1.2rem !important;

    &:hover {
        background: rgba(42, 183, 175, 0.12) !important;
        border-color: ${BRAND_PRIMARY} !important;
    }
`;

const ConfirmButton = styled(IconButton)`
    background: ${BRAND_PRIMARY} !important;
    color: white !important;
    width: 2rem !important;
    height: 2rem !important;
    font-size: 1rem !important;

    &:hover {
        background: ${BRAND_SECONDARY} !important;
    }
`;

const ProfessionalsListContainer = styled.div`
    max-height: 400px;
    overflow-y: auto;
    padding-top: var(--spacer-XS);
    padding-right: var(--spacer-XS);
    margin-bottom: var(--spacer-S);

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: var(--bg-light);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--appLigthGrey);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: ${BRAND_PRIMARY};
    }
`;

const InputsGrid = styled.div<React.PropsWithChildren<{}>>`
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: var(--spacer-XS);
    align-items: center;
    margin-bottom: var(--spacer-S);

    @media (max-width: 540px) {
        grid-template-columns: 1fr;
        gap: var(--spacer-S);
    }
`;

const RoomInputRow = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--spacer-XS);
    align-items: center;

    @media (max-width: 540px) {
        grid-template-columns: 1fr;
    }
`;

const SectionHint = styled.div`
    color: var(--text-muted);
    font-size: 0.9rem;
    margin-bottom: var(--spacer-M);
`;

const LightLabel = styled.div`
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-body);
    margin-bottom: var(--spacer-XS);
`;

const ServiceWithPriceContainer = styled.div<React.PropsWithChildren<{ onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void }>>`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    flex: 1;
    width: 100%;
`;

const PriceInput = styled(TextField)`
    width: 120px;
    flex: 0 0 120px;

    @media (max-width: 540px) {
        width: 84px;
        flex: 0 0 84px;
    }

    & .MuiInputBase-root {
        border-radius: 999px;
        font-size: 0.875rem;
        background: white;
        height: 2rem;
    }

    & .MuiInputBase-input {
        padding: 0.4rem 0.9rem;
        text-align: right;
    }

    & .MuiOutlinedInput-root {
        & fieldset {
            border-color: ${BRAND_PRIMARY};
        }
        &:hover fieldset {
            border-color: ${BRAND_PRIMARY};
        }
        &.Mui-focused fieldset {
            border-color: ${BRAND_PRIMARY};
        }
    }
`;

const ServiceLabel = styled.div`
    color: var(--text-muted);
    font-size: 0.875rem;
    font-weight: 500;
`;

interface OnboardingServiceInput {
    name: string;
    price?: string;
    originalName?: string; // Track which suggestion this came from
}

interface OnboardingProfessionalInput {
    name?: string;
    email?: string;
}

export interface OnboardingData {
    companyName: string;
    services: OnboardingServiceInput[];
    rooms: string[];
    professionals: OnboardingProfessionalInput[];
    notifyProfessionalsByEmail: boolean;
    acceptedTermsAndConditions: boolean;
    acceptedPrivacyPolicy: boolean;
    marketingAcceptedByEmail: boolean;
    adminCredentials?: {
        email: string;
        password: string;
    };
    partnerCode?: string;
    partnerId?: string;
}

interface ProfessionalOnboardingWizardProps {
    isOpen: boolean;
    isLoading?: boolean;
    onComplete: (data: OnboardingData) => void;
    onCancel: () => void;
    onGoogleCredentialSelect?: (credential: string, userInfo?: { email?: string; name?: string; photoUrl?: string }) => void;
    onGoogleCredentialClear?: () => void;
    googleClientId?: string;
    initialData?: {
        companyName?: string;
        email?: string;
        name?: string;
        photoUrl?: string;
        partnerCode?: string;
        partnerId?: string;
    };
}

type AdminAuthMethod = 'google' | 'credentials';

const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const validateCompanyName = (name: string, t: (key: TK) => string): string | null => {
    if (name.trim().length === 0) {
        return t(TK.companyNameRequired);
    }
    if (name.trim().length < 2) {
        return t(TK.companyNameTooShort);
    }
    if (name.trim().length > 100) {
        return t(TK.companyNameTooLong);
    }
    return null;
};

const parseGoogleUserInfoFromCredential = (credential: string): { email?: string; name?: string; photoUrl?: string } => {
    try {
        const payloadPart = credential.split('.')[1];
        if (!payloadPart) {
            return {};
        }

        const padded = payloadPart.padEnd(payloadPart.length + (4 - payloadPart.length % 4) % 4, '=');
        const normalizedBase64 = padded.replace(/-/g, '+').replace(/_/g, '/');
        const decoded = atob(normalizedBase64);
        const payload = JSON.parse(decoded);

        return {
            email: payload?.email,
            name: payload?.name,
            photoUrl: payload?.picture,
        };
    } catch {
        return {};
    }
};

const normalizeGooglePhotoUrl = (photoUrl?: string): string | undefined => {
    if (!photoUrl) {
        return undefined;
    }

    const trimmedUrl = photoUrl.trim();
    if (!trimmedUrl) {
        return undefined;
    }

    if (trimmedUrl.startsWith('//')) {
        return `https:${trimmedUrl}`;
    }

    if (trimmedUrl.startsWith('http://')) {
        return `https://${trimmedUrl.slice('http://'.length)}`;
    }

    return trimmedUrl;
};

const SERVICE_SUGGESTION_KEYS: TK[] = [
    TK.serviceSuggestionPsychology,
    TK.serviceSuggestionPhysiotherapy,
    TK.serviceSuggestionPilates,
    TK.serviceSuggestionPsychiatry,
    TK.serviceSuggestionNutrition,
    TK.serviceSuggestionCoaching,
    TK.serviceSuggestionMassageTherapy,
];

const ROOM_SUGGESTION_KEYS: TK[] = [
    TK.roomSuggestionRoom1,
    TK.roomSuggestionRoom2,
    TK.roomSuggestionOnline,
    TK.roomSuggestionStudio,
    TK.roomSuggestionWellnessRoom,
];

export const ProfessionalOnboardingWizard: FC<ProfessionalOnboardingWizardProps> = ({
    isOpen,
    isLoading,
    onComplete,
    onCancel,
    onGoogleCredentialSelect,
    onGoogleCredentialClear,
    googleClientId,
    initialData
}) => {
    const t = useTranslations();
    const welcomeText = t(TK.welcomeToAppointmint);
    const [currentStep, setCurrentStep] = useState(1);
    const [companyName, setCompanyName] = useState(initialData?.companyName || initialData?.name || '');
    const [showPartnerCodeInputs, setShowPartnerCodeInputs] = useState(!!(initialData?.partnerCode || initialData?.partnerId));
    const [partnerCode, setPartnerCode] = useState(initialData?.partnerCode || '');
    const [partnerId, setPartnerId] = useState(initialData?.partnerId || '');
    const [selectedServices, setSelectedServices] = useState<OnboardingServiceInput[]>([]);
    const [customServices, setCustomServices] = useState<string[]>([]);
    const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
    const [editingServiceName, setEditingServiceName] = useState('');
    const [editingServicePrice, setEditingServicePrice] = useState('');
    const [isAddingNewService, setIsAddingNewService] = useState(false);
    const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
    const [customRooms, setCustomRooms] = useState<string[]>([]);
    const [newRoomInput, setNewRoomInput] = useState('');
    const [showRoomInput, setShowRoomInput] = useState(false);
    const [professionals, setProfessionals] = useState<OnboardingProfessionalInput[]>([{
        name: initialData?.name || '',
        email: initialData?.email || '',
    }]);
    const [notifyProfessionalsByEmail, setNotifyProfessionalsByEmail] = useState(true);
    const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState(false);
    const [acceptedPrivacyPolicy, setAcceptedPrivacyPolicy] = useState(false);
    const [marketingAcceptedByEmail, setMarketingAcceptedByEmail] = useState(false);
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
    const [adminAuthMethod, setAdminAuthMethod] = useState<AdminAuthMethod>(initialData?.email ? 'google' : 'credentials');
    const [isAdminAuthEditorOpen, setIsAdminAuthEditorOpen] = useState(!initialData?.email);
    const [googleCredential, setGoogleCredential] = useState<string | null>(null);
    const [googleAdminInfo, setGoogleAdminInfo] = useState<{ email?: string; name?: string; photoUrl?: string } | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const defaultsInitialized = useRef(false);
    const passwordSectionRef = useRef<HTMLDivElement>(null);
    const checkboxSectionRef = useRef<HTMLDivElement>(null);
    const touchHandledRoomRef = useRef<string | null>(null);
    const wasCredentialsCompleteRef = useRef(false);
    const hydratedInitialDataKeyRef = useRef<string | null>(null);

    const hasGoogleAuthSelected = adminAuthMethod === 'google';
    const isCredentialsComplete = validateEmail(adminEmail) && adminPassword.length >= 8 && adminPassword === adminPasswordConfirm;
    const shouldShowAdminSummary = !isAdminAuthEditorOpen && (hasGoogleAuthSelected || isCredentialsComplete);

    // Set default selections on mount
    useEffect(() => {
        if (!defaultsInitialized.current) {
            // Pre-select Room 1 and Room 2
            const room1 = t(TK.roomSuggestionRoom1);
            const room2 = t(TK.roomSuggestionRoom2);
            setSelectedRooms([room1, room2]);

            defaultsInitialized.current = true;
        }
    }, [t]);

    useEffect(() => {
        if (!isOpen) {
            setGoogleCredential(null);
            setGoogleAdminInfo(null);
            setAdminAuthMethod(initialData?.email ? 'google' : 'credentials');
            setIsAdminAuthEditorOpen(!initialData?.email);
        }
    }, [isOpen, initialData?.email]);

    useEffect(() => {
        if (!initialData?.email) {
            hydratedInitialDataKeyRef.current = null;
        }
    }, [initialData?.email]);

    useEffect(() => {
        if (!isOpen || !initialData?.email) {
            return;
        }

        const hydrationKey = `${initialData.email}|${initialData.name || ''}|${initialData.companyName || ''}|${initialData.photoUrl || ''}`;
        if (hydratedInitialDataKeyRef.current === hydrationKey) {
            return;
        }

        hydratedInitialDataKeyRef.current = hydrationKey;

        setAdminAuthMethod('google');
        setIsAdminAuthEditorOpen(false);
        setGoogleAdminInfo({
            email: initialData.email,
            name: initialData.name,
            photoUrl: initialData.photoUrl,
        });

        setCompanyName(prev => prev.trim().length > 0 ? prev : (initialData.companyName || initialData.name || ''));
        setAdminEmail(prev => prev.trim().length > 0 ? prev : initialData.email || '');
        setProfessionals(prev => {
            const hasManualProfessionalInput = prev.some(professional =>
                (professional.name && professional.name.trim().length > 0) ||
                (professional.email && professional.email.trim().length > 0)
            );

            if (hasManualProfessionalInput) {
                return prev;
            }

            return [{
                name: initialData.name || '',
                email: initialData.email || '',
            }];
        });

        setErrors(prev => {
            const nextErrors = { ...prev };
            delete nextErrors.adminEmail;
            delete nextErrors.adminPassword;
            delete nextErrors.adminPasswordConfirm;
            return nextErrors;
        });
    }, [isOpen, initialData?.email, initialData?.name, initialData?.companyName, initialData?.photoUrl]);

    useEffect(() => {
        if (hasGoogleAuthSelected) {
            wasCredentialsCompleteRef.current = false;
            return;
        }

        if (!isCredentialsComplete) {
            setIsAdminAuthEditorOpen(true);
            wasCredentialsCompleteRef.current = false;
            return;
        }

        if (!wasCredentialsCompleteRef.current && isAdminAuthEditorOpen) {
            setIsAdminAuthEditorOpen(false);
        }

        wasCredentialsCompleteRef.current = true;
    }, [hasGoogleAuthSelected, isCredentialsComplete, isAdminAuthEditorOpen]);

    const handleGoogleCredentialSelect = (credential: string) => {
        const parsedUserInfo = parseGoogleUserInfoFromCredential(credential);
        setGoogleCredential(credential);
        setGoogleAdminInfo(parsedUserInfo);
        setAdminAuthMethod('google');
        setIsAdminAuthEditorOpen(false);
        setErrors(prev => {
            const nextErrors = { ...prev };
            delete nextErrors.adminEmail;
            delete nextErrors.adminPassword;
            delete nextErrors.adminPasswordConfirm;
            return nextErrors;
        });
        onGoogleCredentialSelect?.(credential, parsedUserInfo);
    };

    const activateCredentialsAuth = () => {
        if (adminAuthMethod === 'credentials') {
            return;
        }

        setGoogleCredential(null);
        setGoogleAdminInfo(null);
        setAdminAuthMethod('credentials');
        onGoogleCredentialClear?.();
    };

    const validateStep = (step: number): { isValid: boolean; errors: { [key: string]: string } } => {
        const newErrors: { [key: string]: string } = {};

        if (step === 1) {
            const companyNameError = validateCompanyName(companyName, t);
            if (companyNameError) {
                newErrors.companyName = companyNameError;
            }
        } else if (step === 2) {
            if (selectedServices.length === 0) {
                newErrors.services = t(TK.atLeastOneService);
            }

            if (selectedRooms.length === 0) {
                newErrors.rooms = t(TK.atLeastOneRoom);
            }
        } else if (step === 3) {
            const filledProfessionals = professionals.filter(p => (p.name && p.name.trim().length > 0) || (p.email && p.email.trim().length > 0));
            if (filledProfessionals.length === 0) {
                newErrors.professionals = t(TK.atLeastOneProfessional);
            } else {
                const invalidEmails = filledProfessionals
                    .filter(p => p.email && p.email.trim().length > 0)
                    .filter(p => !validateEmail(p.email as string));

                if (invalidEmails.length > 0) {
                    newErrors.professionals = t(TK.invalidEmail);
                }
            }

            if (!hasGoogleAuthSelected) {
                if (!adminEmail || adminEmail.trim().length === 0) {
                    newErrors.adminEmail = t(TK.emailRequired);
                } else if (!validateEmail(adminEmail)) {
                    newErrors.adminEmail = t(TK.invalidEmail);
                }

                if (!adminPassword || adminPassword.trim().length === 0) {
                    newErrors.adminPassword = t(TK.passwordRequired);
                } else if (adminPassword.length < 8) {
                    newErrors.adminPassword = t(TK.passwordTooShort);
                }

                if (!adminPasswordConfirm || adminPasswordConfirm.trim().length === 0) {
                    newErrors.adminPasswordConfirm = t(TK.passwordRequired);
                } else if (adminPassword !== adminPasswordConfirm) {
                    newErrors.adminPasswordConfirm = t(TK.passwordsDoNotMatch);
                }
            }

            // Validate mandatory consents
            if (!acceptedTermsAndConditions || !acceptedPrivacyPolicy) {
                newErrors.consents = t(TK.youMustAcceptTermsAndPrivacy);
            }
        }

        setErrors(newErrors);
        return {
            isValid: Object.keys(newErrors).length === 0,
            errors: newErrors,
        };
    };

    const handleNext = () => {
        const validation = validateStep(currentStep);
        if (validation.isValid) {
            if (currentStep < 3) {
                setCurrentStep(currentStep + 1);
            } else {
                handleFinish();
            }
        } else if (currentStep === 3) {
            // Scroll to the relevant section based on error type
            setTimeout(() => {
                if (validation.errors.adminEmail || validation.errors.adminPassword || validation.errors.adminPasswordConfirm) {
                    setIsAdminAuthEditorOpen(true);
                    passwordSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else if (validation.errors.consents) {
                    checkboxSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    };

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleFinish = () => {
        if (validateStep(3).isValid) {
            const normalizedProfessionals = professionals
                .filter(p => (p.name && p.name.trim().length > 0) || (p.email && p.email.trim().length > 0))
                .map(p => ({
                    name: p.name?.trim(),
                    email: p.email?.trim(),
                }));

            const normalizedServices = selectedServices.map(s => ({
                name: s.name,
                price: s.price,
            }));

            const data: OnboardingData = {
                companyName: companyName.trim(),
                services: normalizedServices,
                rooms: selectedRooms,
                professionals: normalizedProfessionals,
                notifyProfessionalsByEmail,
                acceptedTermsAndConditions,
                acceptedPrivacyPolicy,
                marketingAcceptedByEmail,
                partnerCode: partnerCode.trim() || undefined,
                partnerId: partnerId.trim() || undefined,
            };

            // Add admin credentials for non-Google sign-up
            if (!hasGoogleAuthSelected) {
                data.adminCredentials = {
                    email: adminEmail.trim(),
                    password: adminPassword,
                };
            }

            onComplete(data);
        }
    };

    const toggleService = (serviceName: string) => {
        setSelectedServices(prev => {
            const existingIndex = prev.findIndex(s => s.originalName === serviceName || s.name === serviceName);
            if (existingIndex >= 0) {
                // Remove service
                setEditingServiceIndex(null);
                setEditingServiceName('');
                setEditingServicePrice('');
                setIsAddingNewService(false);
                return prev.filter((_, i) => i !== existingIndex);
            }
            // Add service and start editing both name and price
            const newService = { name: serviceName, originalName: serviceName };
            const newIndex = prev.length;
            setEditingServiceIndex(newIndex);
            setEditingServiceName(serviceName);
            setEditingServicePrice('');
            setIsAddingNewService(false);
            return [...prev, newService];
        });
    };

    const startAddingNewService = () => {
        setIsAddingNewService(true);
        setEditingServiceIndex(null);
        setEditingServiceName('');
        setEditingServicePrice('');
    };

    const finalizePriceEdit = () => {
        const name = editingServiceName.trim();
        const price = editingServicePrice.trim();

        if (!name) {
            // Cancel without changes
            setEditingServiceIndex(null);
            setEditingServiceName('');
            setEditingServicePrice('');
            setIsAddingNewService(false);
            return;
        }

        if (isAddingNewService) {
            // Adding a new custom service
            const serviceSuggestions = SERVICE_SUGGESTION_KEYS.map(key => t(key));
            const allServices = [...serviceSuggestions, ...customServices];

            if (!allServices.includes(name)) {
                setCustomServices(prev => [...prev, name]);
            }

            const newService = { name, price: price || undefined, originalName: name };
            setSelectedServices(prev => [...prev, newService]);
            setIsAddingNewService(false);
        } else if (editingServiceIndex !== null) {
            // Editing existing service
            setSelectedServices(prev => prev.map((service, i) =>
                i === editingServiceIndex
                    ? { name, price: price || undefined, originalName: service.originalName }
                    : service
            ));
        }

        setEditingServiceIndex(null);
        setEditingServiceName('');
        setEditingServicePrice('');
    };

    const handlePriceInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            finalizePriceEdit();
        }
    };

    const handleServiceEditBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        // Only close if focus moved outside the container
        const currentTarget = e.currentTarget;
        const relatedTarget = e.relatedTarget as HTMLElement;

        // Don't finalize if clicking the confirm button (it will handle finalization)
        if (relatedTarget && relatedTarget.closest('[aria-label="' + t(TK.confirm) + '"]')) {
            return;
        }

        setTimeout(() => {
            if (!currentTarget.contains(document.activeElement)) {
                finalizePriceEdit();
            }
        }, 0);
    };

    const handleServiceDoubleClick = (index: number) => {
        const service = selectedServices[index];
        setIsAddingNewService(false);
        setEditingServiceIndex(index);
        setEditingServiceName(service.name);
        setEditingServicePrice(service.price || '');
    };

    const toggleRoom = (roomName: string) => {
        setSelectedRooms(prev => {
            if (prev.includes(roomName)) {
                return prev.filter(r => r !== roomName);
            }
            return [...prev, roomName];
        });
    };

    const handleRoomTouchEnd = (event: React.TouchEvent<HTMLButtonElement>, roomName: string) => {
        event.preventDefault();
        touchHandledRoomRef.current = roomName;
        toggleRoom(roomName);
    };

    const handleRoomClick = (roomName: string) => {
        if (touchHandledRoomRef.current === roomName) {
            touchHandledRoomRef.current = null;
            return;
        }
        toggleRoom(roomName);
    };

    const addCustomRoom = () => {
        const trimmed = newRoomInput.trim();
        if (!trimmed) return;

        const roomSuggestions = ROOM_SUGGESTION_KEYS.map(key => t(key));
        const allRooms = [...roomSuggestions, ...customRooms];
        if (!allRooms.includes(trimmed)) {
            setCustomRooms(prev => [...prev, trimmed]);
            setSelectedRooms(prev => [...prev, trimmed]);
        } else if (!selectedRooms.includes(trimmed)) {
            setSelectedRooms(prev => [...prev, trimmed]);
        }
        setNewRoomInput('');
        setShowRoomInput(false);
    };

    const handleRoomInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addCustomRoom();
        }
    };

    const addProfessional = () => {
        if (professionals.length >= MAX_PROFESSIONALS) {
            return;
        }
        setProfessionals([...professionals, { name: '', email: '' }]);
    };

    const removeProfessional = (index: number) => {
        if (professionals.length === 1) {
            setProfessionals([{ name: '', email: '' }]);
            return;
        }
        setProfessionals(professionals.filter((_, i) => i !== index));
    };

    const updateProfessionalName = (index: number, value: string) => {
        setProfessionals(prev => prev.map((professional, i) => i === index ? { ...professional, name: value } : professional));
    };

    const updateProfessionalEmail = (index: number, value: string) => {
        setProfessionals(prev => prev.map((professional, i) => i === index ? { ...professional, email: value } : professional));
    };

    const handleProfessionalKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>, index: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (professionals.length < MAX_PROFESSIONALS &&
                ((professionals[index].name?.trim().length ?? 0) > 0 || (professionals[index].email?.trim().length ?? 0) > 0)) {
                addProfessional();
            }
        }
    };

    const renderStepIndicator = () => (
        <StepIndicator>
            <StepCircle $active={currentStep === 1} $completed={currentStep > 1}>
                <StepNumber>1</StepNumber>
            </StepCircle>
            <StepLine $completed={currentStep > 1} />
            <StepCircle $active={currentStep === 2} $completed={currentStep > 2}>
                <StepNumber>2</StepNumber>
            </StepCircle>
            <StepLine $completed={currentStep > 2} />
            <StepCircle $active={currentStep === 3} $completed={false}>
                <StepNumber>3</StepNumber>
            </StepCircle>
        </StepIndicator>
    );

    const renderStep1 = () => (
        <>
            <StepHeader>
                <p>{t(TK.letsSetupYourAccount)}</p>
            </StepHeader>
            <TextField
                id="onboarding-company-name"
                fullWidth
                label={t(TK.companyName)}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        handleNext();
                    }
                }}
                placeholder={t(TK.companyNamePlaceholder)}
                autoFocus
                error={!!errors.companyName}
                helperText={errors.companyName}
                variant="outlined"
            />
            <div style={{ marginTop: 'var(--spacer-XS)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--spacer-XXS)' }}>
                <Button
                    size="small"
                    onClick={() => {
                        if (showPartnerCodeInputs) {
                            setPartnerCode('');
                            setPartnerId('');
                        }
                        setShowPartnerCodeInputs(!showPartnerCodeInputs);
                    }}
                    style={{ color: 'var(--text-muted)', textTransform: 'none', padding: 0, minWidth: 0, fontSize: '0.8rem' }}
                >
                    {t(TK.addPartnerCode)}
                </Button>
                <StyledTooltip title={t(TK.partnerCodeTooltip)} placement="top" {...TOOLTIP_TOUCH_PROPS}>
                    <InfoIconButton aria-label={t(TK.partnerCodeTooltip)}>
                        ⓘ
                    </InfoIconButton>
                </StyledTooltip>
            </div>
            {showPartnerCodeInputs && (
                <div style={{ marginTop: 'var(--spacer-S)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                    <TextField
                        id="onboarding-partner-code"
                        fullWidth
                        label={t(TK.partnerCode)}
                        value={partnerCode}
                        onChange={(e) => setPartnerCode(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        id="onboarding-partner-id"
                        fullWidth
                        label={`${t(TK.partnerId)} (${t(TK.optional)})`}
                        value={partnerId}
                        onChange={(e) => setPartnerId(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                </div>
            )}
        </>
    );

    const renderStep2 = () => {
        const getServiceDisplay = (suggestionName: string) => {
            const service = selectedServices.find(s => s.originalName === suggestionName || s.name === suggestionName);
            if (service) {
                if (service.price) {
                    return `${service.name} - ${service.price}€`;
                }
                return service.name;
            }
            return suggestionName;
        };

        return (
            <>
                <StepHeader>
                    <h3>{t(TK.servicesProvided)}</h3>
                    <p>{t(TK.servicesOptionalDescription)}</p>
                </StepHeader>
                <SectionCard>
                    <SectionTitle>
                        {t(TK.yourServices)}
                        <StyledTooltip title={t(TK.addMoreDetailsLater)} placement="top" {...TOOLTIP_TOUCH_PROPS}>
                            <InfoIconButton aria-label={t(TK.addMoreDetailsLater)}>
                                ⓘ
                            </InfoIconButton>
                        </StyledTooltip>
                    </SectionTitle>
                    <SuggestionGroup>
                        {!isAddingNewService && editingServiceIndex === null && (
                            <>
                                {SERVICE_SUGGESTION_KEYS.map(key => {
                                    const serviceName = t(key);
                                    const serviceIndex = selectedServices.findIndex(s => s.originalName === serviceName || s.name === serviceName);
                                    const isSelected = serviceIndex >= 0;
                                    return (
                                        <SuggestionButton
                                            key={serviceName}
                                            variant="outlined"
                                            size="small"
                                            $selected={isSelected}
                                            onClick={() => toggleService(serviceName)}
                                            onDoubleClick={(e) => {
                                                if (isSelected) {
                                                    e.stopPropagation();
                                                    handleServiceDoubleClick(serviceIndex);
                                                }
                                            }}
                                        >
                                            {getServiceDisplay(serviceName)}
                                        </SuggestionButton>
                                    );
                                })}
                                {customServices.map(serviceName => {
                                    const serviceIndex = selectedServices.findIndex(s => s.originalName === serviceName || s.name === serviceName);
                                    const isSelected = serviceIndex >= 0;
                                    return (
                                        <SuggestionButton
                                            key={serviceName}
                                            variant="outlined"
                                            size="small"
                                            $selected={isSelected}
                                            onClick={() => toggleService(serviceName)}
                                            onDoubleClick={(e) => {
                                                if (isSelected) {
                                                    e.stopPropagation();
                                                    handleServiceDoubleClick(serviceIndex);
                                                }
                                            }}
                                        >
                                            {getServiceDisplay(serviceName)}
                                        </SuggestionButton>
                                    );
                                })}
                            </>
                        )}
                        <AddCustomContainer $fullWidth={isAddingNewService || editingServiceIndex !== null}>
                            {(isAddingNewService || editingServiceIndex !== null) ? (
                                <ServiceWithPriceContainer onBlur={handleServiceEditBlur}>
                                    <AddCustomInput
                                        id="onboarding-service-name"
                                        placeholder={t(TK.serviceName)}
                                        value={editingServiceName}
                                        onChange={(e) => setEditingServiceName(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                finalizePriceEdit();
                                            }
                                        }}
                                        variant="outlined"
                                        size="small"
                                        style={{ flex: 1 }}
                                        autoFocus
                                    />
                                    <PriceInput
                                        id="onboarding-service-price"
                                        placeholder="PVP"
                                        value={editingServicePrice}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setEditingServicePrice(value);
                                        }}
                                        onKeyPress={handlePriceInputKeyPress}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">€</InputAdornment>,
                                        }}
                                    />
                                    <ConfirmButton
                                        id="onboarding-service-confirm"
                                        size="small"
                                        onClick={finalizePriceEdit}
                                        aria-label={t(TK.confirm)}
                                    >
                                        ✓
                                    </ConfirmButton>
                                </ServiceWithPriceContainer>
                            ) : (
                                <AddButton
                                    id="onboarding-add-service"
                                    size="small"
                                    onClick={startAddingNewService}
                                    aria-label={t(TK.addCustomService)}
                                >
                                    +
                                </AddButton>
                            )}
                        </AddCustomContainer>
                    </SuggestionGroup>
                    {errors.services && <ErrorText>{errors.services}</ErrorText>}
                </SectionCard>

                <SectionCard>
                    <SectionTitle>
                        {t(TK.yourRooms)}
                        <StyledTooltip title={t(TK.roomsOptionalDescription)} placement="top" {...TOOLTIP_TOUCH_PROPS}>
                            <InfoIconButton aria-label={t(TK.roomsOptionalDescription)}>
                                ⓘ
                            </InfoIconButton>
                        </StyledTooltip>
                    </SectionTitle>
                    <SuggestionGroup>
                        {ROOM_SUGGESTION_KEYS.map(key => {
                            const room = t(key);
                            return (
                                <SuggestionButton
                                    key={room}
                                    variant="outlined"
                                    size="small"
                                    $selected={selectedRooms.includes(room)}
                                    onTouchEnd={(event) => handleRoomTouchEnd(event, room)}
                                    onClick={() => handleRoomClick(room)}
                                >
                                    {room}
                                </SuggestionButton>
                            );
                        })}
                        {customRooms.map(room => (
                            <SuggestionButton
                                key={room}
                                variant="outlined"
                                size="small"
                                $selected={selectedRooms.includes(room)}
                                onTouchEnd={(event) => handleRoomTouchEnd(event, room)}
                                onClick={() => handleRoomClick(room)}
                            >
                                {room}
                            </SuggestionButton>
                        ))}
                        <AddCustomContainer $fullWidth={showRoomInput}>
                            {showRoomInput && (
                                <AddCustomInput
                                    id="onboarding-room-input"
                                    placeholder={t(TK.addCustomRoom)}
                                    value={newRoomInput}
                                    onChange={(e) => setNewRoomInput(e.target.value)}
                                    onKeyPress={handleRoomInputKeyPress}
                                    variant="outlined"
                                    size="small"
                                    autoFocus
                                />
                            )}
                            <AddButton
                                id="onboarding-add-room"
                                size="small"
                                onClick={() => {
                                    if (showRoomInput && newRoomInput.trim()) {
                                        addCustomRoom();
                                    } else {
                                        setShowRoomInput(!showRoomInput);
                                        if (!showRoomInput) {
                                            setNewRoomInput('');
                                        }
                                    }
                                }}
                            >
                                +
                            </AddButton>
                        </AddCustomContainer>
                    </SuggestionGroup>
                    {errors.rooms && <ErrorText>{errors.rooms}</ErrorText>}
                </SectionCard>
            </>
        );
    };

    const renderStep3 = () => (
        <>
            <StepHeader>
                <h3>{t(TK.configureAccessAndProfessionals)}</h3>
            </StepHeader>

            {shouldShowAdminSummary ? (
                <SectionCard>
                    <AdminCardHeader>
                        <SectionTitle>{t(TK.admin)}</SectionTitle>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                            {hasGoogleAuthSelected && (
                                <AdminAuthBadge>
                                    <AdminAuthBadgeIcon>G</AdminAuthBadgeIcon>
                                    Google
                                </AdminAuthBadge>
                            )}
                            <EditAuthButton
                                size="small"
                                onClick={() => setIsAdminAuthEditorOpen(true)}
                                aria-label={t(TK.edit)}
                            >
                                <EditIcon fontSize="small" />
                            </EditAuthButton>
                        </div>
                    </AdminCardHeader>
                    <AdminGoogleCard>
                        <ListAvatar
                            src={normalizeGooglePhotoUrl(googleAdminInfo?.photoUrl || initialData?.photoUrl)}
                            alt={t(TK.admin)}
                            displayName={googleAdminInfo?.name || adminEmail || googleAdminInfo?.email || initialData?.name || initialData?.email || t(TK.admin)}
                            username={googleAdminInfo?.name || initialData?.name}
                            email={googleAdminInfo?.email || adminEmail || initialData?.email}
                        />
                        <AdminInfo>
                            <AdminLabel>{t(TK.admin)}</AdminLabel>
                            {(googleAdminInfo?.name || initialData?.name) && <AdminName>{googleAdminInfo?.name || initialData?.name}</AdminName>}
                            <AdminEmail>{googleAdminInfo?.email || adminEmail || initialData?.email || '-'}</AdminEmail>
                        </AdminInfo>
                    </AdminGoogleCard>
                </SectionCard>
            ) : (
                <SectionCard ref={passwordSectionRef}>
                    <SectionTitle>
                        {t(TK.adminCredentials)}
                        <StyledTooltip title={t(TK.theseWillBeYourAdminCredentials)} placement="top" {...TOOLTIP_TOUCH_PROPS}>
                            <InfoIconButton aria-label={t(TK.theseWillBeYourAdminCredentials)}>
                                ⓘ
                            </InfoIconButton>
                        </StyledTooltip>
                    </SectionTitle>
                    {googleClientId && (
                        <div style={{ marginTop: 'var(--spacer-S)' }}>
                            <GoogleButtonWrapper>
                                <GoogleSignInButton
                                    clientId={googleClientId}
                                    onSuccess={handleGoogleCredentialSelect}
                                    onError={(error) => console.error('Google Sign-In error:', error)}
                                    disabled={isLoading}
                                />
                            </GoogleButtonWrapper>
                            <AuthDivider>{t(TK.or)}</AuthDivider>
                        </div>
                    )}
                    <StyledTextField
                        id="onboarding-admin-email"
                        fullWidth
                        type="email"
                        label={t(TK.email)}
                        value={adminEmail}
                        onChange={(e) => {
                            activateCredentialsAuth();
                            setAdminEmail(e.target.value);
                        }}
                        placeholder={t(TK.yourEmail)}
                        autoFocus
                        variant="outlined"
                        size="small"
                        error={!!errors.adminEmail}
                        helperText={errors.adminEmail}
                    />
                    <StyledTextField
                        id="onboarding-admin-password"
                        fullWidth
                        type="password"
                        label={t(TK.password)}
                        value={adminPassword}
                        onChange={(e) => {
                            activateCredentialsAuth();
                            setAdminPassword(e.target.value);
                        }}
                        placeholder={t(TK.yourPassword)}
                        variant="outlined"
                        size="small"
                        error={!!errors.adminPassword}
                        helperText={errors.adminPassword}
                    />
                    <StyledTextField
                        id="onboarding-admin-password-confirm"
                        fullWidth
                        type="password"
                        label={t(TK.confirmPassword)}
                        value={adminPasswordConfirm}
                        onChange={(e) => {
                            activateCredentialsAuth();
                            setAdminPasswordConfirm(e.target.value);
                        }}
                        placeholder={t(TK.confirmPassword)}
                        variant="outlined"
                        size="small"
                        error={!!errors.adminPasswordConfirm}
                        helperText={errors.adminPasswordConfirm}
                    />
                </SectionCard>
            )}

            <SectionCard>
                <SectionTitle>
                    {t(TK.inviteProfessionals)}
                    <StyledTooltip title={t(TK.professionalsOptionalDescription)} placement="top" {...TOOLTIP_TOUCH_PROPS}>
                        <InfoIconButton aria-label={t(TK.professionalsOptionalDescription)}>
                            ⓘ
                        </InfoIconButton>
                    </StyledTooltip>
                </SectionTitle>
                <ProfessionalsListContainer>
                    {professionals.map((professional, index) => (
                        <InputsGrid key={index}>
                            <Step3TextField
                                id={`onboarding-professional-${index}-name`}
                                fullWidth
                                value={professional.name || ''}
                                onChange={(e) => updateProfessionalName(index, e.target.value)}
                                onKeyPress={(e) => handleProfessionalKeyPress(e, index)}
                                placeholder={t(TK.name)}
                                label={t(TK.name)}
                                variant="outlined"
                                size="small"
                            />
                            <Step3TextField
                                id={`onboarding-professional-${index}-email`}
                                fullWidth
                                type="email"
                                label={t(TK.professionalEmailOptional)}
                                value={professional.email || ''}
                                onChange={(e) => updateProfessionalEmail(index, e.target.value)}
                                onKeyPress={(e) => handleProfessionalKeyPress(e, index)}
                                placeholder={t(TK.professionalEmailsPlaceholder)}
                                variant="outlined"
                                size="small"
                                error={!!errors.professionals && errors.professionals === t(TK.invalidEmail)}
                            />
                            {professionals.length > 1 && (
                                <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={() => removeProfessional(index)}
                                    aria-label={t(TK.removeProfessional)}
                                >
                                    ✕
                                </IconButton>
                            )}
                        </InputsGrid>
                    ))}
                </ProfessionalsListContainer>
                <AddButtonContainer>
                    <StyledTooltip
                        title={professionals.length >= MAX_PROFESSIONALS ? t(TK.maxProfessionalsReached) : ''}
                        placement="top"
                        {...TOOLTIP_TOUCH_PROPS}
                    >
                        <span>
                            <ButtonStyled
                                variant="outlined"
                                size="small"
                                onClick={addProfessional}
                                disabled={professionals.length >= MAX_PROFESSIONALS}
                            >
                                + {t(TK.addProfessional)}
                            </ButtonStyled>
                        </span>
                    </StyledTooltip>
                </AddButtonContainer>
                {errors.professionals && <ErrorText>{errors.professionals}</ErrorText>}
                {professionals.some(p => p.email && p.email.trim().length > 0) && (
                    <CheckboxContainer>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    id="onboarding-notify-professionals"
                                    color="primary"
                                    checked={notifyProfessionalsByEmail}
                                    onChange={(event) => setNotifyProfessionalsByEmail(event.target.checked)}
                                />
                            )}
                            label={t(TK.notifyProfessionalsByEmail)}
                        />
                    </CheckboxContainer>
                )}
            </SectionCard>

            <SectionCard ref={checkboxSectionRef}>
                <SectionTitle>
                    {t(TK.termsAndConditions)} & {t(TK.privacyPolicy)}
                </SectionTitle>
                <CheckboxContainer style={{ borderTop: 'none', paddingTop: 0 }}>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                id="onboarding-accept-terms"
                                color="primary"
                                checked={acceptedTermsAndConditions}
                                onChange={(event) => setAcceptedTermsAndConditions(event.target.checked)}
                            />
                        )}
                        label={
                            <span>
                                {t(TK.iAcceptThe)}{' '}
                                <a
                                    href={`https://appointmint.pt/${getLanguage()}/terms`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--appBlue)' }}
                                >
                                    {t(TK.termsAndConditions)}
                                </a>
                            </span>
                        }
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                id="onboarding-accept-privacy"
                                color="primary"
                                checked={acceptedPrivacyPolicy}
                                onChange={(event) => setAcceptedPrivacyPolicy(event.target.checked)}
                            />
                        )}
                        label={
                            <span>
                                {t(TK.iAcknowledgeThe)}{' '}
                                <a
                                    href={`https://appointmint.pt/${getLanguage()}/privacy`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: 'var(--appBlue)' }}
                                >
                                    {t(TK.privacyPolicy)}
                                </a>
                            </span>
                        }
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                color="primary"
                                checked={marketingAcceptedByEmail}
                                onChange={(event) => setMarketingAcceptedByEmail(event.target.checked)}
                            />
                        )}
                        label={t(TK.iWantToReceiveFeatureUpdates)}
                    />
                    {errors.consents && <ErrorText>{errors.consents}</ErrorText>}
                </CheckboxContainer>
            </SectionCard>
        </>
    );

    return (
        <>
            <Dialog
                open={isOpen}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                disableEscapeKeyDown
                onClose={(_, reason) => {
                    if (reason !== 'backdropClick') {
                        onCancel();
                    }
                }}
                PaperProps={{
                    style: {
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '80vh',
                        maxHeight: '80vh',
                    },
                }}
            >
                <WizardHeader>
                    <CloseButton
                        onClick={onCancel}
                        disabled={isLoading}
                        aria-label={t(TK.closeOnboardingWizard)}
                    >
                        ✕
                    </CloseButton>
                </WizardHeader>
            <DialogContentStyled>
                {currentStep === 1 && (
                    <HeroContainer>
                        <HeroTitle>{welcomeText}</HeroTitle>
                        <TrialBadge aria-label={t(TK.startYour30DayTrial)}>
                            {t(TK.startYour30DayTrial)}
                        </TrialBadge>
                    </HeroContainer>
                )}
                <WizardContainer>
                    {renderStepIndicator()}
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                </WizardContainer>
                </DialogContentStyled>
                <DialogActionsStyled>
                    <div>
                        {currentStep > 1 && (
                            <IconButton
                                onClick={handlePrevious}
                                disabled={isLoading}
                                aria-label={t(TK.goToPreviousStep)}
                            >
                                ←
                            </IconButton>
                        )}
                    </div>
                    <PrimaryActionButton id="onboarding-next-button" variant="contained" onClick={handleNext} disabled={isLoading}>
                        {isLoading ? t(TK.creatingYourAccount) : currentStep === 3 ? t(TK.finish) : t(TK.next)}
                    </PrimaryActionButton>
                </DialogActionsStyled>
            </Dialog>
        </>
    );
};
